import React, { useCallback, useState } from 'react';

import { BuilderComponent } from '@builder.io/react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { BUILDER_MODELS } from '~/builder/constants/models';
import CloseButton from '~/shared/components/CloseButton';
import FableticsModal from '~/shared/components/FableticsModal';
import { mobile } from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';

const ModalCloseButton = styled(CloseButton)`
  color: ${({ theme }) => theme.colors.mysteryBoxModalCloseButton};
  position: absolute;
  top: 0;
  right: 0;
  padding: 18px;
  z-index: 1;
  transition: opacity 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.mysteryBoxModalCloseButton};
    opacity: 0.7;
  }

  &:focus {
    color: ${({ theme }) => theme.colors.mysteryBoxModalCloseButton};
  }
`;

const BackgroundWrapper = styled.div`
  background-image: url(${({ $desktopSrc }) => $desktopSrc});
  background-size: cover;
  background-position: top;
  position: relative;
  width: 520px;
  height: 742px;
  display: flex;
  justify-content: center;
  align-items: end;
  border-radius: 24px;
  overflow: hidden;

  ${mobile`
    background-image: url(${({ $mobileSrc }) => $mobileSrc});
    height: 520px;
  `}
`;

const dialogStyle = `
  background: transparent;
  box-shadow: none;
`;

const modalContentWrapperStyles = `
  background: transparent;
  position: relative;
  margin: 0 15px;
`;

const transitionProps = {
  enter: { opacity: 1 },
  from: { opacity: 0 },
  leave: { opacity: 0 },
};

const DetailsChip = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.mysteryBoxLabel};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  white-space: nowrap;
`;

export function MysteryBoxDetailsChip({ onClick }) {
  const intl = useIntl();
  return (
    <DetailsChip
      type="button"
      onClick={onClick}
      aria-label={intl.formatMessage({
        id: 'site_pdp.mystery_box_details',
        defaultMessage: 'Details',
      })}
    >
      {intl.formatMessage({
        id: 'site_pdp.mystery_box_details',
        defaultMessage: 'Details',
      })}
    </DetailsChip>
  );
}

MysteryBoxDetailsChip.propTypes = {
  onClick: PropTypes.func.isRequired,
};

export default function MysteryBoxDetailsModal({ detailsModal }) {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const closeLabel = intl.formatMessage({
    id: 'global_cta.close',
    defaultMessage: 'Close',
  });

  const desktopSrc = detailsModal?.data?.desktopBackgroundImage;
  const mobileSrc = detailsModal?.data?.mobileBackgroundImage || desktopSrc;

  return (
    <>
      <MysteryBoxDetailsChip onClick={open} />
      <FableticsModal
        contentWrapperStyles={modalContentWrapperStyles}
        dialogStyle={dialogStyle}
        isOpen={isOpen}
        onExit={close}
        closeButton={<ModalCloseButton aria-label={closeLabel} />}
        showCloseButton
        title={intl.formatMessage({
          id: 'site_pdp.mystery_box_details',
          defaultMessage: 'Details',
        })}
        transitionEnter={transitionProps.enter}
        transitionFrom={transitionProps.from}
        transitionLeave={transitionProps.leave}
        variant="unpadded"
      >
        <BackgroundWrapper $desktopSrc={desktopSrc} $mobileSrc={mobileSrc}>
          <BuilderComponent
            model={BUILDER_MODELS.MYSTERY_BOX_MODAL_DETAILS}
            content={detailsModal}
          />
        </BackgroundWrapper>
      </FableticsModal>
    </>
  );
}

MysteryBoxDetailsModal.propTypes = {
  detailsModal: PropTypes.object.isRequired,
};
